import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Sparkles, LineChart, Cpu, Radio, BookOpen, Ticket } from "lucide-react";
import api from "@/lib/api";
import { IMG } from "@/lib/imagery";
import { useOwned } from "@/lib/useOwned";
import Seo from "@/components/Seo";
import { usePromo, savingsFor } from "@/components/PromoStrip";

const ICON_BY_SLUG = {
  indicators: LineChart,
  "expert-advisors": Cpu,
  signals: Radio,
  courses: BookOpen,
};

export default function CategoryPage() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const promo = usePromo();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/categories/${slug}`),
      api.get(`/products?category=${slug}`),
    ])
      .then(([c, p]) => {
        setCategory(c.data);
        setProducts(p.data);
      })
      .catch(() => setError("Not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="p-24 text-center text-emerald-100/50">Loading…</div>;
  if (error || !category) return <div className="p-24 text-center text-emerald-100/50">Category not found.</div>;

  const Icon = ICON_BY_SLUG[category.slug] || LineChart;
  const live = !category.coming_soon;

  return (
    <div className="relative">
      <Seo title={category.name} description={category.description} image={category.hero_image} />
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[color:var(--ink-hairline)]">
        <div className="pointer-events-none absolute inset-0">
          <img
            src={category.hero_image || IMG.charts}
            alt=""
            className="h-full w-full object-cover"
            style={{ filter: "saturate(1.05) contrast(1.02)" }}
          />
          <div className="absolute inset-0 hero-scrim" />
        </div>
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-5 pt-24 pb-16 lg:px-8 lg:pt-32 lg:pb-20">
          <div className="pl-6 lg:pl-10">
            <div className="vline absolute left-5 top-24 h-20 w-[2px] lg:left-8 lg:top-32" />
            <div className="mb-6 flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-500/12 text-lime-300">
                <Icon size={22} />
              </span>
              <p className="overline">/ {category.slug}</p>
            </div>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <h1 className="font-editorial text-5xl font-medium tracking-tight text-white sm:text-6xl lg:text-7xl"
                  data-testid={`category-${slug}-title`}>
                {category.name}
              </h1>
              <span data-testid={`category-${slug}-status`} className={`chip ${live ? "chip-emerald" : "chip-amber"}`}>
                {live ? "Live" : "Coming soon"}
              </span>
            </div>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-emerald-100/70 sm:text-lg">
              {category.description}
            </p>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="relative mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        {!live && <ComingSoonState name={category.name} description={category.description} />}
        {live && products.length === 0 && <EmptyLive name={category.name} description={category.description} />}
        {live && products.length > 0 && <ProductGrid products={products} promo={promo} />}      </section>
    </div>
  );
}

function ProductGrid({ products, promo }) {
  const owned = useOwned();
  return (
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {products.map((p, i) => {
        const isOwned = owned.has(p.id);
        return (
        <motion.div
          key={p.id}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: i * 0.05 }}
        >
          <Link
            to={p.category_slug === "courses" ? `/course/${p.slug}` : `/product/${p.slug}`}
            data-testid={`product-card-${p.slug}`}
            className="card-edge group relative flex h-full flex-col overflow-hidden transition-transform hover:-translate-y-1"
          >
            <div className="relative h-44 overflow-hidden">
              <img
                src={p.image_url || IMG.matrix}
                alt=""
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                style={{ filter: "saturate(1.05) contrast(1.03)" }}
              />
              <div className="absolute inset-0"
                   style={{ background: "linear-gradient(180deg, rgba(10,42,25,0) 45%, rgba(10,42,25,0.35) 100%)" }} />
              <span className="absolute right-3 top-3 chip chip-emerald">
                {p.license_type === "account_bound" ? "Account-bound" : "One-time"}
              </span>
              {isOwned && (
                <span className="absolute left-3 top-3 chip chip-emerald" data-testid={`card-owned-${p.slug}`}>Owned</span>
              )}
            </div>
            <div className="flex flex-1 flex-col p-6">
              <p className="overline mb-2">{p.category_slug}</p>
              <h3 className="font-editorial text-xl font-medium tracking-tight text-white">
                {p.title}
              </h3>
              {p.subtitle && <p className="mt-2 text-sm text-emerald-100/60">{p.subtitle}</p>}
              {savingsFor(promo, p.price) > 0 && (
                <p className="mt-3 inline-flex items-center gap-1 self-start rounded-full bg-emerald-500/12 px-2.5 py-1 text-[11px] font-medium text-lime-300" data-testid={`card-save-${p.slug}`}>
                  <Ticket size={11} /> Save ${savingsFor(promo, p.price).toFixed(2)} with {promo.code}
                </p>
              )}
              <div className="mt-auto flex items-end justify-between pt-6">
                <div>
                  <p className="font-mono-edge text-[10px] uppercase tracking-widest text-emerald-100/50">Price</p>
                  <p className="font-editorial text-2xl text-lime-300">
                    ${p.price.toFixed(2)}
                    <span className="ml-1 text-xs text-emerald-100/50">{p.currency}</span>
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 text-sm text-emerald-300">
                  {isOwned ? "Owned" : "View"} <ArrowUpRight size={14} />
                </span>
              </div>
            </div>
          </Link>
        </motion.div>
        );
      })}
    </div>
  );
}

function EmptyLive({ name, description }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid gap-10 lg:grid-cols-[1.3fr_1fr]">
      <div className="card-edge p-8 lg:p-10">
        <p className="overline mb-4">Preview</p>
        <h2 className="font-editorial text-3xl font-medium tracking-tight text-white sm:text-4xl">
          {name} catalog is loading.
        </h2>
        <p className="mt-5 max-w-xl text-sm leading-relaxed text-emerald-100/60">{description}</p>
        <p className="mt-8 rounded-xl border border-dashed border-emerald-500/25 bg-emerald-500/5 p-5 text-sm text-emerald-200/80">
          <Sparkles size={14} className="mr-2 inline text-emerald-300" />
          Admin has not published products yet. They will appear here automatically.
        </p>
      </div>
      <div className="grid gap-4">
        {[
          { k: "Deliverables", v: "MQL5/MQL4 binaries + setup guide + change-log per release." },
          { k: "Licensing", v: "Account-bound (MT4/MT5 login number) — non-transferable and revocable." },
          { k: "Fulfilment", v: "Automatic on payment webhook — no manual approvals." },
        ].map((r) => (
          <div key={r.k} className="card-moss p-6">
            <p className="overline mb-2">{r.k}</p>
            <p className="text-sm text-emerald-100/70">{r.v}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function ComingSoonState({ name, description }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mx-auto max-w-3xl">
      <div className="card-moss relative overflow-hidden p-10 text-center lg:p-14">
        <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full border border-amber-400/25 bg-amber-400/10 text-amber-300">
          <Clock size={22} />
        </div>
        <h2 className="mt-6 font-editorial text-4xl font-medium tracking-tight text-white">
          {name} — coming soon
        </h2>
        <p className="mx-auto mt-5 max-w-lg text-base leading-relaxed text-emerald-100/70">{description}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/register" className="btn-edge">Notify me <ArrowUpRight size={14} /></Link>
          <Link to="/store/expert-advisors" className="btn-edge-ghost">Browse what’s live</Link>
        </div>
      </div>
    </motion.div>
  );
}
